/**
 * Utility functions for reordering items and managing hierarchical drag-and-drop data.
 *
 * @module components/sim/dragDrop/helpers
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */

/**
 * Reorders an item in a list from a specified start index to an end index.
 *
 * This function creates a new array by copying the original list,
 * removes an item from the specified `startIndex`, and re-inserts
 * it at the specified `endIndex`. The original list remains
 * unmodified.
 *
 * @param {Array} list - The array containing items to be reordered.
 * @param {number} startIndex - The index of the item to be removed.
 * @param {number} endIndex - The index where the removed item should be inserted.
 * @returns {Array} A new array with the item reordered.
 */
export const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed); // inserting task in new index
    return result;
};

/**
 * Inserts a new item into an array at the specified index without modifying the original array.
 *
 * @param {Array} arr - The original array to copy and modify.
 * @param {number} index - The index at which the new item should be inserted.
 *                         If the index is out of bounds, it will insert at the closest valid position.
 * @param {*} newItem - The item to insert into the array.
 * @returns {Array} A new array with the new item inserted at the specified index.
 */
export const insert = (arr, index, newItem) => [
    // part of the array before the specified index
    ...arr.slice(0, index),
    // inserted item
    newItem,
    // part of the array after the specified index
    ...arr.slice(index),
];

/**
 * Rearranges the hierarchical structure of child elements within a nested data structure.
 *
 * This function recursively reorders elements by moving an item from its current position
 * to a new position within the given `children` object. The reordering is determined by
 * the `splitDropZonePath` and `splitItemPath` parameters, which indicate the paths to
 * the target drop zone and the item to move, respectively.
 *
 * @param {Object} children - The current hierarchical data structure containing children elements.
 * @param {Array<string|number>} splitDropZonePath - An array representing the path to the drop zone
 * where the item should be moved. The path is split into segments, each representing a key or index.
 * @param {Array<string|number>} splitItemPath - An array representing the path to the item to move.
 * The path is split into segments, each representing a key or index.
 * @returns {Object} - A new hierarchical data structure with the specified item moved to the
 * designated location. Other properties of the structure remain unchanged.
 */
export const reorderChildren = (children, splitDropZonePath, splitItemPath) => {
    if (splitDropZonePath.length === 1) {
        const dropZoneIndex = Number(splitDropZonePath[0]);
        const itemIndex = Number(splitItemPath[0]);
        return reorder(children, itemIndex, dropZoneIndex);
    }

    // Update the specific node's children
    const splitDropZoneChildrenPath = splitDropZonePath.slice(1);
    const splitItemChildrenPath = splitItemPath.slice(1);
    const abc = reorderChildren(
        children.scripts,
        splitDropZoneChildrenPath,
        splitItemChildrenPath,
    );

    const newData = {
        createdAt: children.createdAt,
        deleted: children.deleted,
        hint_text: children.hint_text,
        keyword_score: children.keyword_score,
        name: children.name,
        order: children.order,
        real_time_feedback: children.real_time_feedback,
        sim_id: children.sim_id,
        updatedAt: children.updatedAt,
        _id: children._id,
        scripts: abc,
    };
    return newData;
};

/**
 * Adds a child item to the children of a specified drop zone within a layout structure.
 *
 * This function recursively navigates through the layout hierarchy using the given path
 * to locate the appropriate drop zone and inserts the provided item. If the path length
 * is one, the item is directly inserted into the `scripts` array at the specified index.
 * For longer paths, it traverses deeper into the layout by calling itself with the
 * remaining path segments and updates the children accordingly.
 *
 * @param {object} layout - The layout object containing a hierarchical structure with scripts.
 * @param {Array<string>} splitDropZonePath - An array of strings representing the path to the target drop zone.
 *                                            The first element corresponds to the index in the `scripts` array, and
 *                                            subsequent elements represent deeper levels in the hierarchy.
 * @param {object} item - The item to be added to the children at the specified drop zone path.
 * @returns {object} The updated layout structure with the item added to the relevant drop zone.
 */
export const addChildToChildren = (layout, splitDropZonePath, item) => {

    if (splitDropZonePath.length === 1) {
        const dropZoneIndex = Number(splitDropZonePath[0]);
        return insert(layout.scripts, dropZoneIndex, item);
    }

    // Update the specific node's children
    const splitItemChildrenPath = splitDropZonePath.slice(1);

    const data = addChildToChildren(layout, splitItemChildrenPath, item);
    const newData = {
        scripts: data,
    };
    return newData;
};

/**
 * Handles the reordering of items within a parent container in a layout structure.
 *
 * @param {Array} layout - The hierarchical layout structure representing the parent container and its children.
 * @param {Array<number>} splitDropZonePath - An array of indices representing the path to the target drop zone within the layout.
 * @param {Array<number>} splitItemPath - An array of indices representing the path to the item being moved within the layout.
 * @returns {Array} - The updated layout structure after reordering the children within the parent container.
 */
export const handleMoveWithinParent = (
    layout,
    splitDropZonePath,
    splitItemPath,
) => reorderChildren(layout, splitDropZonePath, splitItemPath);

/**
 * Moves a sidebar component into a parent container in the layout structure.
 *
 * This function updates the layout by inserting a specified item into the location
 * defined by the split drop zone path. Based on the length of the path array,
 * the function determines how the item should be integrated into the layout hierarchy.
 *
 * @param {Object} layout - The current layout structure to be updated.
 * @param {Array} splitDropZonePath - An array representing the path to the drop zone within the layout.
 * @param {Object} item - The sidebar component to be moved into the parent container.
 * @returns {Object} The updated layout structure with the moved sidebar component.
 */
export const handleMoveSidebarComponentIntoParent = (
    layout,
    splitDropZonePath,
    item,
) => {
    let newLayoutStructure;
    switch (splitDropZonePath.length) {
        case 2: {
            newLayoutStructure = item;
            break;
        }
        default: {
            newLayoutStructure = item;
        }
    }
    return addChildToChildren(layout, splitDropZonePath, newLayoutStructure);
};
